import {RELATED_CUSTOM_NOTICES_TAG, invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {buildCustomNoticeTag} from './buildCustomNoticeTag';
import {composeRelatedCustomNoticesUrl} from './composeRelatedCustomNoticesUrl';
import {
  CreateCustomNoticeApiArg,
  CreateCustomNoticeApiResponse,
  CreateCustomNoticeApiResponseSchema,
  DeleteCustomNoticeApiArg,
  DeleteCustomNoticeApiResponse,
  DeleteCustomNoticeApiResponseSchema,
  GetCustomNoticePermissionsApiArg,
  GetCustomNoticePermissionsApiResponse,
  GetCustomNoticePermissionsApiResponseSchema,
  GetCustomNoticesApiArg,
  GetCustomNoticesApiResponse,
  GetCustomNoticesApiResponseSchema,
  GetRelatedCustomNoticesApiArg,
  GetRelatedCustomNoticesApiResponse,
  GetRelatedCustomNoticesApiResponseSchema,
  UpdateCustomNoticeApiArg,
  UpdateCustomNoticeApiResponse,
  UpdateCustomNoticeApiResponseSchema,
} from './types';

export const customNoticeApi = omneticApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    getCustomNotices: build.query<GetCustomNoticesApiResponse, GetCustomNoticesApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/custom-notice/${queryArg.resourceId}/${encodeURIComponent(queryArg.recordId)}`,
      }),
      providesTags: (_result, _error, queryArg) => [buildCustomNoticeTag(queryArg)],
      extraOptions: {
        responseSchema: GetCustomNoticesApiResponseSchema,
      },
    }),
    getCustomNoticePermissions: build.query<
      GetCustomNoticePermissionsApiResponse,
      GetCustomNoticePermissionsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/custom-notice/${queryArg.resourceId}/${encodeURIComponent(queryArg.recordId)}/permissions`,
      }),
      providesTags: ['permissions'],
      extraOptions: {
        responseSchema: GetCustomNoticePermissionsApiResponseSchema,
      },
    }),
    getRelatedCustomNotices: build.query<
      GetRelatedCustomNoticesApiResponse,
      GetRelatedCustomNoticesApiArg
    >({
      query: (queryArg) => ({
        url: composeRelatedCustomNoticesUrl(queryArg.resourceId, queryArg.recordId),
      }),
      providesTags: (result, _error, queryArg) => [
        RELATED_CUSTOM_NOTICES_TAG,
        buildCustomNoticeTag(queryArg),
        ...(result ?? []).map((notice) => buildCustomNoticeTag(notice.source)),
      ],
      extraOptions: {
        responseSchema: GetRelatedCustomNoticesApiResponseSchema,
      },
    }),
    createCustomNotice: build.mutation<CreateCustomNoticeApiResponse, CreateCustomNoticeApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/custom-notice/${queryArg.resourceId}/${encodeURIComponent(queryArg.recordId)}`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: CreateCustomNoticeApiArg) => [
        buildCustomNoticeTag(queryArg),
        RELATED_CUSTOM_NOTICES_TAG,
      ]),
      extraOptions: {
        responseSchema: CreateCustomNoticeApiResponseSchema,
      },
    }),
    updateCustomNotice: build.mutation<UpdateCustomNoticeApiResponse, UpdateCustomNoticeApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/custom-notice/${queryArg.resourceId}/${encodeURIComponent(queryArg.recordId)}/${encodeURIComponent(queryArg.customNoticeId)}`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: UpdateCustomNoticeApiArg) => [
        buildCustomNoticeTag(queryArg),
        RELATED_CUSTOM_NOTICES_TAG,
      ]),
      extraOptions: {
        responseSchema: UpdateCustomNoticeApiResponseSchema,
      },
    }),
    deleteCustomNotice: build.mutation<DeleteCustomNoticeApiResponse, DeleteCustomNoticeApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/custom-notice/${queryArg.resourceId}/${encodeURIComponent(queryArg.recordId)}/${encodeURIComponent(queryArg.customNoticeId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: DeleteCustomNoticeApiArg) => [
        buildCustomNoticeTag(queryArg),
        RELATED_CUSTOM_NOTICES_TAG,
      ]),
      extraOptions: {
        responseSchema: DeleteCustomNoticeApiResponseSchema,
      },
    }),
  }),
});

export const {
  useGetCustomNoticesQuery,
  useGetCustomNoticePermissionsQuery,
  useGetRelatedCustomNoticesQuery,
  useCreateCustomNoticeMutation,
  useUpdateCustomNoticeMutation,
  useDeleteCustomNoticeMutation,
} = customNoticeApi;
